import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import Slider from 'rc-slider'
import styles from './SingleSlider.module.css'
import cn from 'classnames'

const StyledSlider = styled(Slider)`
  .rc-slider-rail {
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: transparent;
    border-radius: 0;
  }

  .rc-slider-track {
    position: absolute;
    left: 0;
    height: 2px;
    background-color: transparent;
    border-radius: 0;
    transform: translateX(0);
  }

  .rc-slider-handle {
    position: absolute;
    width: 27px;
    height: 16px;
    background: var(--color-white);
    border-radius: var(--border-radius-4);
    margin-top: calc(-1*var(--spacing-2));
    cursor: grab;
    touch-action: pan-x;
    border: 2px solid var(--color-black);
    transform: translateX(-50%);
    transition: background 0.2s;
  }

  .rc-slider-handle:hover {
    background: var(--color-light-gray);
  }

  .rc-slider-handle:active {
    background-color: var(--color-black);
  }

  .rc-slider-step {
    position: absolute;
    width: 100%;
    height: 2px;
    background: transparent;
  }
`

interface SingleSliderProps {
  min: number
  max: number
  value: number
  onChange: (value: number) => void
  label?: string
  step?: number
  decimalPlaces?: number
  percent?: number
  formatValue?: (num: number) => string
  parseValue?: (value: string) => number
}

const SingleSlider: React.FC<SingleSliderProps> = ({
  min,
  max,
  value,
  onChange,
  label,
  step = 1,
  percent,
  decimalPlaces = 0,
  formatValue = (val) =>
    val.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }),
  parseValue = (val) => parseFloat(val.replace(/ /g, ''))
}) => {
  const [inputValue, setInputValue] = useState<string>(formatValue(value))
  const [isDefaultValue, setIsDefaultValue] = useState(true)

  const prevInputValueRef = useRef<string>(inputValue)

  useEffect(() => {
    const formattedValue = formatValue(value)
    if (formattedValue !== prevInputValueRef.current) {
      setInputValue(formattedValue)
    }

    setIsDefaultValue(value === min)
    prevInputValueRef.current = formattedValue
  }, [value, formatValue, min])

  const handleInputChange = (newValue: string) => {
    const sanitizedValue = newValue.replace(/[^0-9 ]/g, '')
    setInputValue(sanitizedValue)
  }

  const handleInputBlur = () => {
    const parsedValue = parseValue(inputValue)
    if (isNaN(parsedValue)) {
      setInputValue(formatValue(value))
      return
    }

    const roundedValue = Math.round(parsedValue / step) * step
    onChange(Math.max(min, Math.min(roundedValue, max)))
  }

  const handleSliderChange = (sliderValue: number | number[]) => {
    const roundedValue =
      Math.round(
        (Array.isArray(sliderValue) ? sliderValue[0] : sliderValue) / step
      ) * step
    onChange(roundedValue)
  }

  return (
    <div className={styles.slider_wrapper}>
      <label className={cn('text_captions_c2', styles.range_label)}>
        {' '}
        <span className={styles.range_label_span}>{label}</span>
      </label>
      <div className={styles.range_indicators}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          className={cn('text_subheaders_sh3', isDefaultValue ? styles.input_inactive : styles.input_active)}
        />
        {percent !== undefined && (
          <span className={cn('text_subheaders_sh3', styles.percent_label)}>{percent}%</span>
        )}
      </div>
      <div className={styles.range_slider}>
        <StyledSlider
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleSliderChange}
        />
      </div>
    </div>
  )
}

export default SingleSlider
